import React, { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AgentCard } from "./AgentCard";
import { ShowcaseItem } from '@/types/showcase';
import { showcaseService } from '@/services/showcase/showcaseService';
import { interactionService } from '@/services/showcase/interactionService';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth/AuthContext';

interface FeaturedAgentsProps {
  limit?: number;
  autoRotate?: boolean;
  autoRotateInterval?: number;
  className?: string;
}

/**
 * Carousel component for displaying featured agents in the showcase
 */
export const FeaturedAgents: React.FC<FeaturedAgentsProps> = ({
  limit = 6,
  autoRotate = true,
  autoRotateInterval = 5000,
  className,
}) => {
  const [items, setItems] = useState<ShowcaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [userLikes, setUserLikes] = useState<Record<string, boolean>>({});
  const { user } = useAuth();

  // Fetch featured items
  useEffect(() => {
    const fetchFeaturedItems = async () => {
      try {
        setLoading(true);
        const featuredItems = await showcaseService.getFeaturedItems(limit);
        setItems(featuredItems);
        setError(null);

        // If user is logged in, check which items they've liked
        if (user) {
          const interactions = await interactionService.getUserInteractions(user.id, 'like');
          const likesMap: Record<string, boolean> = {};
          interactions.forEach(interaction => {
            likesMap[interaction.itemId] = true;
          });
          setUserLikes(likesMap);
        }
      } catch (err) {
        console.error('Error fetching featured items:', err);
        setError('Failed to load featured agents. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedItems();
  }, [limit, user]);

  // Auto-rotation logic
  useEffect(() => {
    if (!autoRotate || items.length <= 1) return;

    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % items.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [autoRotate, autoRotateInterval, items.length]);

  // Navigation handlers
  const handlePrevious = () => {
    setActiveIndex(prev => (prev - 1 + items.length) % items.length);
  };

  const handleNext = () => {
    setActiveIndex(prev => (prev + 1) % items.length);
  };

  // Interaction handlers
  const handleLike = async (id: string) => {
    if (!user) return;
    
    try {
      const isLiked = userLikes[id];
      
      if (isLiked) {
        await interactionService.removeInteraction(user.id, id, 'like');
        setUserLikes(prev => ({ ...prev, [id]: false }));
        
        // Update like count in the UI
        setItems(prev => 
          prev.map(item => 
            item.id === id ? { ...item, likes: Math.max(0, (item.likes || 0) - 1) } : item
          )
        );
      } else {
        await interactionService.recordInteraction({
          userId: user.id,
          itemId: id,
          interactionType: 'like',
        });
        setUserLikes(prev => ({ ...prev, [id]: true }));
        
        // Update like count in the UI
        setItems(prev => 
          prev.map(item => 
            item.id === id ? { ...item, likes: (item.likes || 0) + 1 } : item
          )
        );
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleFork = async (id: string) => {
    if (!user) return;
    
    try {
      // Open the fork modal or redirect to the fork page
      // This would typically be handled by a more complex UI flow
      const forkedItemId = await interactionService.forkShowcaseItem(id, user.id);
      
      // Redirect to the new forked item
      window.location.href = `/showcase/${forkedItemId}`;
    } catch (err) {
      console.error('Error forking item:', err);
    }
  };

  const handleView = async (id: string) => {
    try {
      // Record view interaction
      if (user) {
        await interactionService.recordInteraction({
          userId: user.id,
          itemId: id,
          interactionType: 'view',
        });
      }
      
      // Increment view count regardless of user login status
      await showcaseService.incrementViewCount(id);
    } catch (err) {
      console.error('Error recording view:', err);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className={cn("w-full p-8 flex justify-center items-center", className)}>
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-8 w-64 bg-muted rounded"></div>
          <div className="h-64 w-full max-w-3xl bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={cn("w-full p-8 flex justify-center items-center text-center", className)}>
        <div className="text-destructive">
          <p>{error}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Render empty state
  if (items.length === 0) {
    return (
      <div className={cn("w-full p-8 text-center", className)}>
        <h3 className="text-lg font-medium">No featured agents available</h3>
        <p className="text-muted-foreground mt-2">
          Check back later for featured content
        </p>
      </div>
    );
  }

  return (
    <div className={cn("w-full py-6", className)}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Featured Agents</h2>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevious}
            disabled={items.length <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Previous</span>
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={handleNext}
            disabled={items.length <= 1}
          >
            <ChevronRight className="h-4 w-4" />
            <span className="sr-only">Next</span>
          </Button>
        </div>
      </div>
      
      <div className="relative overflow-hidden">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {items.map((item, index) => (
            <div 
              key={item.id} 
              className="w-full flex-shrink-0 px-2"
            >
              <AgentCard
                item={item}
                onLike={handleLike}
                onFork={handleFork}
                onView={handleView}
                isLiked={userLikes[item.id!]}
                className="max-w-md mx-auto"
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Pagination indicators */}
      <div className="flex justify-center gap-1 mt-4">
        {items.map((_, index) => (
          <button
            key={index}
            className={cn(
              "w-2 h-2 rounded-full transition-colors",
              index === activeIndex ? "bg-primary" : "bg-muted"
            )}
            onClick={() => setActiveIndex(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};